import { useState, useEffect } from "react";

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow+Condensed:wght@300;400;600;700&family=Barlow:wght@300;400;500;600&display=swap');

  :root {
    --olive: #4a5240;
    --dark-olive: #2d3326;
    --forest: #3a4a2e;
    --gold: #c8a84b;
    --gold-light: #e8c96b;
    --sand: #d4c5a0;
    --cream: #f0e8d0;
    --charcoal: #1a1e17;
    --charcoal2: #13160f;
    --red: #8b1c1c;
    --muted: rgba(212,197,160,0.55);
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    background: var(--charcoal);
    color: var(--cream);
    font-family: 'Barlow', sans-serif;
    overflow-x: hidden;
    scroll-behavior: smooth;
  }

  /* scrollbar */
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--charcoal2); }
  ::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 3px; }

  /* reveal */
  .reveal { opacity: 0; transform: translateY(28px); transition: opacity .7s ease, transform .7s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }

  /* utility */
  .section-tag { font-family:'Barlow Condensed',sans-serif; font-size:.75rem; letter-spacing:4px; text-transform:uppercase; color:var(--gold); margin-bottom:.4rem; }
  .section-title { font-family:'Bebas Neue',sans-serif; font-size:clamp(2.4rem,5vw,4rem); letter-spacing:4px; line-height:.95; margin-bottom:.8rem; }
  .section-line { width:56px; height:3px; background:var(--gold); margin-bottom:1.8rem; }
  .section-desc { font-size:.97rem; line-height:1.85; color:var(--sand); max-width:620px; }
  
  .btn {
    padding:.85rem 2.4rem; font-family:'Barlow Condensed',sans-serif;
    font-size:.88rem; letter-spacing:3px; text-transform:uppercase;
    cursor:pointer; border:none; transition:all .28s; display:inline-block; text-decoration:none;
  }
  .btn-primary { background:var(--gold); color:var(--charcoal); font-weight:700; }
  .btn-primary:hover { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 8px 24px rgba(200,168,75,.25); }
  .btn-secondary { background:transparent; color:var(--gold); border:1px solid var(--gold); }
  .btn-secondary:hover { background:rgba(200,168,75,.1); transform:translateY(-2px); }

  @keyframes fadeDown { from{opacity:0;transform:translateY(-18px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeUp   { from{opacity:0;transform:translateY(18px)}  to{opacity:1;transform:translateY(0)} }
  @keyframes rotateSlow { from{transform:translate(-50%,-50%) rotate(0deg)} to{transform:translate(-50%,-50%) rotate(360deg)} }
  @keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
`;

// ─── HOOK REVEAL ────────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// NAVBAR
// ═══════════════════════════════════════════════════════════════════════════
  const pages = [
    { id: "inicio",      label: "Inicio" },
    { id: "bienvenida",  label: "Bienvenida" },
    { id: "mision",      label: "Misión" },
    { id: "valores",     label: "Valores" },
    { id: "fuerzas",     label: "Fuerzas" },
    { id: "historia",    label: "Historia" },
    { id: "noticias",    label: "Noticias" },
    { id: "enlistarse",  label: "Enlistarse"},
  ];

function Navbar({ current, onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const nav = {
    position: "fixed", top: 0, width: "100%", zIndex: 999,
    background: scrolled ? "rgba(19,22,15,0.96)" : "rgba(19,22,15,0.7)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid rgba(200,168,75,0.35)",
    transition: "background .3s",
  };

  return (
    <nav style={nav}>
      <div style={{ maxWidth:1400, margin:"0 auto", padding:"0 2rem", display:"flex", alignItems:"center", justifyContent:"space-between", height:62 }}>
        {/* Logo */}
        <button onClick={() => onNav("inicio")} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
          <img src="/logo.png" alt="descripción" style={{ width: "23%", height: "23%"}} />
        </button>

        {/* Desktop links */}
        <ul style={{ display:"flex", gap:"0.15rem", listStyle:"none", alignItems:"center" }} className="desktop-nav">
          {pages.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => onNav(p.id)}
                style={{
                  background: current === p.id ? "rgba(200,168,75,0.12)" : "none",
                  border: "none", cursor:"pointer",
                  color: current === p.id ? "var(--gold)" : "var(--sand)",
                  fontFamily:"'Barlow Condensed',sans-serif",
                  fontSize:"0.82rem", letterSpacing:"2px", textTransform:"uppercase",
                  padding:"0.45rem 0.85rem",
                  borderBottom: current === p.id ? "2px solid var(--gold)" : "2px solid transparent",
                  transition:"all .2s",
                }}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{ display:"none", background:"none", border:"none", cursor:"pointer", color:"var(--gold)", fontSize:"1.6rem" }}
          className="hamburger"
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ background:"rgba(13,16,11,0.98)", borderTop:"1px solid rgba(200,168,75,.2)", padding:"1rem 2rem 1.5rem" }}>
          {pages.map((p) => (
            <button
              key={p.id}
              onClick={() => { onNav(p.id); setMenuOpen(false); }}
              style={{
                display:"block", width:"100%", textAlign:"left",
                background:"none", border:"none", cursor:"pointer",
                color: current === p.id ? "var(--gold)" : "var(--sand)",
                fontFamily:"'Barlow Condensed',sans-serif",
                fontSize:"1rem", letterSpacing:"2px", textTransform:"uppercase",
                padding:"0.7rem 0",
                borderBottom:"1px solid rgba(200,168,75,.08)",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @media(max-width:900px) {
          .desktop-nav { display:none !important; }
          .hamburger { display:block !important; }
        }
      `}</style>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: INICIO
// ═══════════════════════════════════════════════════════════════════════════
function PageInicio({ onNav }) {
  useReveal();
  return (
    <div>
      {/* HERO */}
      <div style={{
        minHeight:"100vh", position:"relative", overflow:"hidden",
        background:"linear-gradient(135deg,#080b06 0%,#141a0f 45%,#1e2a17 100%)",
        display:"flex", alignItems:"center", justifyContent:"center",
        paddingTop:62,
      }}>
      <video autoPlay muted loop playsInline src="/a.mp4" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.45, zIndex: 0, }}/>
        {/* Pattern */}
        <div style={{
          position:"absolute", inset:0,
          backgroundImage:"repeating-linear-gradient(45deg,transparent,transparent 50px,rgba(200,168,75,0.025) 50px,rgba(200,168,75,0.025) 51px)",
          pointerEvents:"none",
        }}/>
        {/* Rings */}
        {[600,450,300].map((s,i) => (
          <div key={i} style={{
            position:"absolute", width:s, height:s,
            border:"1px solid rgba(200,168,75,0.07)",
            borderRadius:"50%", top:"50%", left:"50%",
            transform:"translate(-50%,-50%)",
            animation:`rotateSlow ${30+i*10}s linear infinite`,
          }}/>
        ))}

        <div style={{ textAlign:"center", position:"relative", zIndex:2, padding:"2rem 1.5rem" }}>
          <div style={{
            display:"inline-block", border:"1px solid var(--gold)",
            padding:"0.3rem 1.4rem", fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:"0.75rem", letterSpacing:"5px", textTransform:"uppercase",
            color:"var(--gold)", marginBottom:"1.6rem",
            animation:"fadeDown .9s ease both",
          }}>
            ⭐ Forjados en disciplina ⭐
          </div>

          <h1 style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:"clamp(4.5rem,13vw,10rem)",
            lineHeight:.88, letterSpacing:"7px",
            color:"var(--cream)",
            textShadow:"0 0 80px rgba(200,168,75,.18)",
            animation:"fadeDown .9s ease .15s both",
          }}>
            1RA Brigada<br/><span style={{ color:"var(--gold)" }}>De servicio</span>
          </h1>

          <p style={{
            fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:"1.1rem", letterSpacing:"4px", textTransform:"uppercase",
            color:"var(--sand)", marginTop:"1.2rem",
            animation:"fadeDown .9s ease .3s both",
          }}>
            Apoyo · Oportuno · Eficaz
          </p>

          <div style={{ marginTop:"2.5rem", display:"flex", gap:"1rem", justifyContent:"center", flexWrap:"wrap", animation:"fadeDown .9s ease .45s both" }}>
            <button className="btn btn-primary" onClick={() => onNav("mision")}>Nuestra Misión</button>
            <button className="btn btn-secondary" onClick={() => onNav("enlistarse")}>Enlistarse</button>
          </div>
        </div>

        {/* Scroll hint */}
        <div style={{
          position:"absolute", bottom:"2.5rem", left:"50%", transform:"translateX(-50%)",
          display:"flex", flexDirection:"column", alignItems:"center", gap:6,
          animation:"pulse 2s ease infinite",
        }}>
          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".7rem", letterSpacing:"3px", color:"var(--muted)", textTransform:"uppercase" }}>Desliza</span>
          <div style={{ width:1, height:40, background:"linear-gradient(to bottom,var(--gold),transparent)" }}/>
        </div>
      </div>

      {/* STATS */}
      <div style={{
        background:"var(--forest)", borderTop:"2px solid var(--gold)", borderBottom:"2px solid var(--gold)",
        display:"flex", flexWrap:"wrap", justifyContent:"space-around", padding:"2rem",
      }}>
        {[
          { num:"50+", label:"Años de Historia" },
          { num:"7",    label:"unidades Militares" },
          { num:"4",   label:"Regiones de operacion" , subLabel: "Piura, Trujillo, Tumbes, Sullana" },
        ].map((s) => (
          <div key={s.label} className="reveal" style={{ textAlign:"center", padding:".6rem 1.2rem" }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"2.6rem", color:"var(--gold)", letterSpacing:2 }}>{s.num}</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".72rem", letterSpacing:"3px", textTransform:"uppercase", color:"var(--sand)" }}>{s.label}</div>
            {s.subLabel && <div style={{ fontSize:".8rem", color:"var(--muted)", marginTop:".5rem" }}>{s.subLabel}</div>}
          </div>
        ))}
      </div>
      <GaleriaComando />
      {/* PREVIEW CARDS */}
      <div style={{ background:"var(--charcoal)", padding:"5rem 2rem" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div className="reveal" style={{ textAlign:"center", marginBottom:"3rem" }}>
            <div className="section-tag">Explora</div>
            <h2 className="section-title">CONOCE NUESTRAS ÁREAS</h2>
            <div className="section-line" style={{ margin:"0 auto 0" }}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:"1.2rem" }}>
            {[
              { title:"Misión", desc:"Conoce los objetivos y compromisos de la 1ra Brigada de Servicios.", page:"mision" },
              { title:"Valores", desc:"Los principios que guían cada decisión y acción de nuestros soldados.", page:"valores" },
              { title:"Fuerzas", desc:"Descubre las ramas y especialidades que componen nuestras fuerzas.", page:"fuerzas" },
              { title:"Historia", desc:"Un recorrido por los momentos más importantes de nuestra Brigada.", page:"historia" },
              { title:"Noticias", desc:"Las últimas novedades y actividades realizadas por el personal militar.", page:"noticias" },
            ].map((c) => (
              <div
                key={c.title} className="reveal"
                onClick={() => onNav(c.page)}
                style={{
                  background:"var(--dark-olive)", border:"1px solid rgba(200,168,75,.15)",
                  padding:"2rem 1.6rem", cursor:"pointer", transition:"all .3s",
                  position:"relative", overflow:"hidden",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.transform="translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(200,168,75,.15)"; e.currentTarget.style.transform="none"; }}
              >
                <div style={{ fontSize:"2.4rem", marginBottom:".9rem" }}>{c.icon}</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.5rem", letterSpacing:3, color:"var(--gold)", marginBottom:".5rem" }}>{c.title}</div>
                <div style={{ fontSize:".88rem", lineHeight:1.7, color:"var(--sand)" }}>{c.desc}</div>
                <div style={{ marginTop:"1.2rem", fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem", letterSpacing:"2px", color:"var(--gold)", textTransform:"uppercase" }}>Ver más →</div>
              </div>
            ))}
          </div>
        </div>
      </div>      
    </div>
  );
}

function GaleriaComando() {
  const [current, setCurrent] = useState(0);

  const comandantes = [
    {
      img: "/9.jpeg",
      nombre: "OSWALDO MARTIN CALLE TALLETO",
      grado: "GENERAL DEL EJERCITO",
      cargo: "COMANDANTE GENERAL DEL EJERCITO",
    },
    {
      img: "/primero.jpeg",
      nombre: "MARCOS ALBERT RODRIGUEZ MONGE",
      grado: "GENERAL DE DIVISION",
      cargo: "JEFE DEL ESTADO MAYOR DEL EJÉRCITO",
    },
        {
      img: "/2.2.jpeg",
      nombre: "JULIO CESAR CATAÑEDA ZEGARRA",
      grado: "GENERAL DE DIVISIÓN",
      cargo: "INSPECTOR GENERAL DEL EJERCITO",
    },
    {
      img: "/2.3.jpeg",
      nombre: "CARLOS ALBERTO RABANAL CALDERON",
      grado: "GENERAL DE DIVISIÓN",
      cargo: "COMANDANTE GENERAL DE LA I DE",
    },
    {
      img: "/2.4.jpeg",
      nombre: "MIGUEL ENRIQUE BURGOS VIEYRA",
      grado: "GENERAL DE LA BRIGADA",
      cargo: "JEFE DE ESTADO MAYOR DE I DE",
    },
        {
      img: "/5.jpeg",
      nombre: "JUAN CARLOS RIVERO CORTIJO",
      grado: "GENERAL DE BRIGADA",
      cargo: "INSPECTOR DE LA I DE",
    },
        {
      img: "/6.jpeg",
      nombre: "ROBERTO FABIO POZO PALOMINO",
      grado: "CORONEL DE INTENDENCIA",
      cargo: "COMANDANTE GENERAL DE LA 1ra BRIG DE SERV",
    },
        {
      img: "/7.jpeg",
      nombre: "JORGE LUIS CAMPOS GOMEZ",
      grado: "TENIENTE CORONEL DE ARTILLERIA",
      cargo: "JEFE DE ESTADO MAYOR DE LA 1ra BRIGADA DE SERVICIOS",
    },
        {
      img: "/8.jpeg",
      nombre: "ALFARO GOICOCHEA JORGE HIBAR",
      grado: "TENIENTE CORONEL DE COMUNICACIONES",
      cargo: "INSPECTOR DE LA 1ra BRIG SERV",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % comandantes.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [comandantes.length]);

  const prev = () => setCurrent((p) => (p - 1 + comandantes.length) % comandantes.length);
  const next = () => setCurrent((p) => (p + 1) % comandantes.length);

  return (
    <div style={{ background:"var(--dark-olive)", padding:"5rem 2rem", borderTop:"2px solid rgba(200,168,75,.2)" }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>

        {/* Título */}
        <div style={{ textAlign:"center", marginBottom:"3rem" }}>
          <div className="section-tag" style={{ textAlign:"center" }}>Liderazgo</div>
          <h2 className="section-title">GALERÍA DE COMANDO</h2>
          <div className="section-line" style={{ margin:"0 auto" }}/>
        </div>

        {/* Carrusel */}
        <div style={{ position:"relative" }}>

          {/* Botón anterior */}
          <button
            onClick={prev}
            style={{
              position:"absolute", left:"-1.5rem", top:"40%", transform:"translateY(-50%)",
              zIndex:10, background:"var(--gold)", border:"none", cursor:"pointer",
              width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center",
              color:"var(--charcoal)", fontSize:"1.2rem", transition:"all .2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background="var(--gold-light)"}
            onMouseLeave={e => e.currentTarget.style.background="var(--gold)"}
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {/* Tarjetas visibles */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"1.5rem", overflow:"hidden" }}>
            {[0, 1, 2].map((offset) => {
              const index = (current + offset) % comandantes.length;
              const c = comandantes[index];
              return (
                <div
                  key={index}
                  style={{
                    background:"var(--charcoal)",
                    border: offset === 1
                      ? "2px solid var(--gold)"
                      : "1px solid rgba(200,168,75,.15)",
                    overflow:"hidden",
                    transform: offset === 1 ? "scale(1.03)" : "scale(1)",
                    transition:"all .5s ease",
                    boxShadow: offset === 1 ? "0 16px 48px rgba(0,0,0,.5)" : "none",
                  }}
                >
                  {/* Foto */}
                  <div style={{ position:"relative", height:320, overflow:"hidden", background:"#1a1e17" }}>
                    <img
                      src={c.img}
                      alt={c.nombre}
                      style={{
                        width:"100%", height:"100%",
                        objectFit:"cover", objectPosition:"center top",
                        transition:"transform .5s ease",
                        filter: offset === 1 ? "none" : "brightness(0.7)",
                      }}
                      onError={e => {
                        e.target.style.display = "none";
                        e.target.parentNode.style.display = "flex";
                        e.target.parentNode.style.alignItems = "center";
                        e.target.parentNode.style.justifyContent = "center";
                        e.target.parentNode.innerHTML = `<i class='bx bx-user' style='font-size:5rem;color:rgba(200,168,75,.3)'></i>`;
                      }}
                    />
                    {/* Overlay degradado */}
                    <div style={{
                      position:"absolute", bottom:0, left:0, right:0, height:"50%",
                      background:"linear-gradient(to top,rgba(10,14,8,.95),transparent)",
                    }}/>
                    {/* Grado encima de la foto */}
                    <div style={{
                      position:"absolute", bottom:"1rem", left:0, right:0, textAlign:"center",
                      fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem",
                      letterSpacing:"3px", textTransform:"uppercase",
                      color: offset === 1 ? "var(--gold)" : "var(--sand)",
                    }}>
                      {c.grado}
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ padding:"1.2rem 1.4rem", textAlign:"center" }}>
                    <div style={{
                      fontFamily:"'Bebas Neue',sans-serif", fontSize:"1rem",
                      letterSpacing:2, color:"var(--cream)", marginBottom:".4rem", lineHeight:1.3,
                    }}>
                      {c.nombre}
                    </div>
                    <div style={{
                      fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem",
                      letterSpacing:"2px", textTransform:"uppercase", color:"var(--muted)",
                      lineHeight:1.4,
                    }}>
                      {c.cargo}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Botón siguiente */}
          <button
            onClick={next}
            style={{
              position:"absolute", right:"-1.5rem", top:"40%", transform:"translateY(-50%)",
              zIndex:10, background:"var(--gold)", border:"none", cursor:"pointer",
              width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center",
              color:"var(--charcoal)", fontSize:"1.2rem", transition:"all .2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background="var(--gold-light)"}
            onMouseLeave={e => e.currentTarget.style.background="var(--gold)"}
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>

        {/* Puntos indicadores */}
        <div style={{ display:"flex", justifyContent:"center", gap:".5rem", marginTop:"2rem" }}>
          {comandantes.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                width: i === current ? 24 : 8,
                height:8, border:"none", cursor:"pointer",
                background: i === current ? "var(--gold)" : "rgba(200,168,75,.25)",
                borderRadius:4, transition:"all .3s",
                padding:0,
              }}
            />
          ))}
        </div>

      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// PAGE: BIENVENIDA
// ═══════════════════════════════════════════════════════════════════════════
function PageBienvenida() {
  useReveal();
  return (
    <div style={{ paddingTop: 62 }}>

      {/* Contenido principal */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "5rem 2rem" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "380px 1fr",
          gap: "4rem", alignItems: "start",
        }}>

          {/* COLUMNA IZQUIERDA — Foto */}
          <div className="reveal" style={{ position: "sticky", top: 90 }}>
            {/* Marco de la foto */}
            <div style={{
              position: "relative",
              border: "2px solid var(--gold)",
              padding: "8px",
              background: "var(--dark-olive)",
            }}>
              {/* Esquinas decorativas */}
              {[
                { top: -6, left: -6 },
                { top: -6, right: -6 },
                { bottom: -6, left: -6 },
                { bottom: -6, right: -6 },
              ].map((pos, i) => (
                <div key={i} style={{
                  position: "absolute", ...pos,
                  width: 14, height: 14,
                  background: "var(--gold)",
                }} />
              ))}

              <img
                src="/fotos/coronel.jpeg" 
                alt="Comandante General"
                style={{
                  width: "100%",
                  aspectRatio: "3/4",
                  objectFit: "cover",
                  objectPosition: "center top",
                  display: "block",
                  filter: "brightness(0.95)",
                }}
                onError={e => {
                  e.target.style.display = "none";
                  e.target.parentNode.style.height = "420px";
                  e.target.parentNode.style.display = "flex";
                  e.target.parentNode.style.alignItems = "center";
                  e.target.parentNode.style.justifyContent = "center";
                  e.target.parentNode.innerHTML += `<i class='bx bx-user' style='font-size:6rem;color:rgba(200,168,75,.25)'></i>`;
                }}
              />
            </div>

            {/* Nombre y cargo debajo de la foto */}
            <div style={{
              marginTop: "1.2rem", textAlign: "center",
              padding: "1.2rem", background: "var(--dark-olive)",
              border: "1px solid rgba(200,168,75,.2)",
            }}>
              <div style={{
                fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.1rem",
                letterSpacing: 3, color: "var(--cream)", marginBottom: ".3rem",
              }}>
                ROBERTO FABIO POZO PALOMINO
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".75rem",
                letterSpacing: "2px", textTransform: "uppercase", color: "var(--gold)",
              }}>
                CORONEL EP
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".75rem",
                letterSpacing: "2px", textTransform: "uppercase", color: "var(--gold)",
              }}>
                Comandante General
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".72rem",
                letterSpacing: "1px", textTransform: "uppercase", color: "var(--muted)",
                marginTop: ".2rem",
              }}>
                1ra Brigada de Servicios — Piura
              </div>
            </div>
          </div>

          {/* COLUMNA DERECHA — Texto */}
          <div className="reveal">
            <h2 className="section-title">MENSAJE DE<br/>BIENVENIDA</h2>
            <div className="section-line" />

            {[
              `Bienvenidos a la página oficial de la 1ra Brigada de Servicios. Unidad de apoyo logístico del Ejército del Perú,  con sede en el Cuartel "ISSAC RODRIGUEZ DE MENDOZA" - El Chipe, en la ciudad de Piura.`,

              `Nuestra brigada tiene como misión fundamental proporcionar apoyo logístico oportuno y eficiente a las unidades de la I División del Ejército, operando en las regiones de Tumbes, Sullana, Piura y Trujillo. Somos el pilar que sostiene la capacidad operativa de las fuerzas bajo nuestro apoyo, garantizando el abastecimiento, el transporte, la sanidad y los servicios que permiten el cumplimiento de la misión.`,

              `Desde su creación como Batallón de Servicios y a lo largo de su destacada participación en los conflictos de 1941, 1981 y 1995, nuestra institución ha forjado una identidad de sacrificio, profesionalismo y entrega a la patria. Hoy, convertidos en la 1ra Brigada de Servicios mediante Decreto Supremo del 07 de octubre de 2011, continuamos honrando ese legado con el más alto sentido de responsabilidad.`,

              `Estamos comprometidos no solo con la excelencia, sino también con el bienestar de nuestro personal, participando activamente en acciones humanitarias, campañas de salud, apoyo ante desastres naturales y actividades cívicas que refuerzan el vínculo entre el Ejército y la población.`,

              `Los invitamos a conocer nuestras unidades, capacidades, historia y valores. Esta es su 1ra Brigada de Servicios.`,
            ].map((p, i) => (
              <p key={i} style={{
                fontSize: ".97rem", lineHeight: 1.9,
                color: "var(--sand)", marginBottom: "1.4rem",
                textAlign: "justify",
                borderLeft: i === 0 ? "3px solid var(--gold)" : "none",
                paddingLeft: i === 0 ? "1.2rem" : 0,
                fontStyle: i === 0 ? "italic" : "normal",
              }}>
                {p}
              </p>
            ))}

            {/* Firma */}
            <div style={{
              marginTop: "2.5rem", textAlign: "right",
              borderTop: "1px solid rgba(200,168,75,.2)", paddingTop: "1.5rem",
            }}>
              <div style={{
                fontFamily: "'Bebas Neue',sans-serif", fontSize: "1rem",
                letterSpacing: 3, color: "var(--cream)",
              }}>
                CORONEL EP ROBERTO FABIO POZO PALOMINO
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".75rem",
                letterSpacing: "2px", textTransform: "uppercase", color: "var(--gold)",
                marginTop: ".2rem",
              }}>
                Comandante General — 1ra Brigada de Servicios
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 768px) {
          .bienvenida-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: MISIÓN
// ═══════════════════════════════════════════════════════════════════════════
function PageMision() {
  useReveal();
  return (
    <div style={{ paddingTop:62 }}>
      {/* Header */}
      <div style={{
        background:"linear-gradient(135deg,#0d110a,#1e2a17)",
        padding:"5rem 2rem 4rem", textAlign:"center",
        borderBottom:"2px solid var(--gold)",
      }}>
        <h1 className="section-title" style={{ fontSize:"clamp(3rem,8vw,6rem)" }}>MISIÓN &amp; VISIÓN</h1>
        <div className="section-line" style={{ margin:"0 auto" }}/>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"5rem 2rem" }}>
        <div style={{ justifyItems:"start", justifyContent:"space-between", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4rem", alignItems:"start" }}>
          <div className="reveal">
            <div className="section-tag">Misión</div>
            <h2 className="section-title">NUESTRA MISIÓN</h2>
            <div className="section-line"/>
            <p className="section-desc" style={{ textAlign:"justify" }}>La Primera Brigada de Servicios proporciona apoyo administrativo a la Primera División, en el corredor de la sierra instalando y operando áreas logísticas con elementos de 
              abastecimientos y un equipo móvil de mantenimiento consolidado en la región Tumbes región Sullana y región Trujillo, simultáneamente desde la región Piura se continuará con el apoyo logístico al
              agrupamiento de artillería con la finalidad facilitar el cumplimiento de la misión de la Primera División del Ejército.</p>
          </div>
          <div className="reveal">
            <div className="section-tag">Visión</div>
            <h2 className="section-title">NUESTRA VISIÓN</h2>
            <div className="section-line"/>
            <p className="section-desc" style={{ textAlign:"justify" }}>Proteger a la nación y los intereses nacionales de cualquier amenaza o agresión empleando el poder militar terrestre, participar en las acciones de SINAGERD, desarrollo sostenible del país, control de orden interno política exterior de acuerdo a la ley, de manera permanente y eficaz; con el fin de contribuir a garantizar la independencia, soberanía, integridad territorial y bienestar de la población.</p>
          </div>
        </div>

        {/* Quote */}
        <div className="reveal" style={{
          margin:"4rem 0", borderLeft:"4px solid var(--gold)", padding:"2rem 2.5rem",
          background:"var(--dark-olive)",
        }}>
          <p style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(1.6rem,3vw,2.2rem)", lineHeight:1.3, letterSpacing:2, color:"var(--cream)" }}>
            "La <span style={{ color:"var(--gold)" }}>valentía</span> no es la ausencia del miedo, sino la determinación de que algo más{" "}
            <span style={{ color:"var(--gold)" }}>importante</span> que el miedo merece ser protegido."
          </p>
          <p style={{ marginTop:"1rem", fontFamily:"'Barlow Condensed',sans-serif", fontSize:".8rem", letterSpacing:"3px", color:"var(--muted)", textTransform:"uppercase" }}>— Doctrina Militar Nacional</p>
        </div>

        {/* Objetivos */}
        <div className="reveal">
          <div className="section-tag">Objetivos</div>
          <h2 className="section-title">OBJETIVOS ESTRATÉGICOS</h2>
          <div className="section-line"/>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginTop:"1rem" }}>
          {[
          { n:"1",  t:"Incrementar las", highlight:"CAPACIDADES MILITARES", t2:" para el empleo del Ejército." },
          { n:"2",  t:"Fortalecer la participación del Ejército en la", highlight:" GESTIÓN DEL RIESGO DE DESASTRES", t2:"." },
          { n:"3",  t:"Fortalecer la participación del Ejército en", highlight:" APOYO AL DESARROLLO SOSTENIBLE", t2:"." },
          { n:"4",  t:"Optimizar la participación del Ejército en el", highlight:" CONTROL DEL ORDEN INTERNO", t2:"." },
          { n:"5",  t:"Desarrollar la", highlight:"CIBERDEFENSA ", t2:"en el Ejército." },
          { n:"6",  t:"Mejorar la", highlight:"EDUCACIÓN Y DOCTRINA", t2:" institucional." },
          { n:"7",  t:"Fortalecer la", highlight:"INVESTIGACIÓN, DESARROLLO E INNOVACIÓN", t2:" en el Ejército." },
          { n:"8",  t:"Incrementar la participación del Ejército en", highlight:" APOYO A LA POLÍTICA EXTERIOR", t2:"." },
          { n:"9",  t:"Mejorar la", highlight:"CULTURA ORGANIZACIONAL", t2:" en el Ejército." },
          { n:"10", t:"Incrementar la", highlight:"CAPACIDAD DE INTELIGENCIA", t2:" en el Ejército." },
          { n:"11", t:"Fortalecer la", highlight:"GESTIÓN INSTITUCIONAL", t2:" en el Ejército." },
          { n:"12", t:"Fortalecer la", highlight:"TRANSFORMACIÓN DIGITAL", t2:" en el Ejército." },
          { n:"13", t:"Mejorar el", highlight:"SISTEMA DE SALUD", t2:" del Ejército." },
          ].map((o) => (
            <div
              key={o.n}
              className="reveal"
              style={{
                display: "flex", alignItems: "center", gap: "1.2rem",
                background: "var(--dark-olive)",
                border: "1px solid rgba(200,168,75,.12)",
                padding: "1rem 1.4rem",
                transition: "all .25s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.background = "rgba(200,168,75,.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(200,168,75,.12)"; e.currentTarget.style.background = "var(--dark-olive)"; }}
            >
              {/* Número */}
              <div style={{
                minWidth: 42, height: 42,
                background: "rgba(200,168,75,.1)",
                border: "1px solid rgba(200,168,75,.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.1rem",
                color: "var(--gold)", letterSpacing: 1, flexShrink: 0,
              }}>
                {o.n}
              </div>

              {/* Línea separadora */}
              <div style={{ width: 2, height: 30, background: "rgba(200,168,75,.2)", flexShrink: 0 }} />

              {/* Texto con highlight */}
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: ".92rem", letterSpacing: 1,
                color: "var(--sand)", lineHeight: 1.4,
              }}>
                {o.t}{" "}
                <span style={{ color:"var(--gold)", fontWeight:700 }}>{o.highlight}</span>
                {o.t2}
              </div> 
            </div>
          ))}
        </div>
        
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: VALORES
// ═══════════════════════════════════════════════════════════════════════════
function PageValores() {
  useReveal();
  const valores = [
    { icon:"bx bxs-medal", name:"Honor", desc:"Actuar con integridad absoluta en el cumplimiento de sus funciones logísticas, manteniendo la ética como base de toda operación de apoyo a la I División del Ejército." },
    { icon:null, img:"/lealtad.png", name:"Lealtad", desc:"Compromiso inquebrantable con el Ejército del Perú, garantizando el apoyo logístico necesario para el éxito de cada misión." },
    { icon:null, img:"/engranaje.png",name:"Eficiencia Logística", desc:"La Brigada opera con precisión y eficacia en el abastecimiento de clases I, III y V, asegurando que cada unidad apoyada reciba los recursos necesarios en tiempo y forma." },
    { icon:"bx bxs-time", name:"Disciplina", desc:"El cumplimiento riguroso de los procedimientos logísticos y administrativos garantiza la eficiencia de las operaciones en las regiones de Tumbes, Sullana, Piura y Trujillo." },
    { icon:null, img:"/servicio.png", name:"Servicio", desc:"El servicio a las unidades combatientes es nuestra razón de ser. Desde Tumbes hasta Trujillo, cada efectivo de la brigada pone el éxito colectivo por encima del interés personal." },
    { icon:"bx bxs-star", name:"Excelencia", desc:"Nuestros euipos de mantenimiento móvil mantienen los más altos estándares técnicos para garantizar la operatividad del material y equipamiento asignado a las unidades apoyadas." },
    { icon:null, img:"/responsabilidad.png", name:"Responsabilidad", desc:"Cada soldado de la brigada asume con plena consciencia la importancia de su función de apoyo, entendiendo que sin logística, no hay operación posible." },
  ];

  return (
    <div style={{ paddingTop:62 }}>
      <div style={{ background:"linear-gradient(135deg,#0d110a,#1e2a17)", padding:"5rem 2rem 4rem", textAlign:"center", borderBottom:"2px solid var(--gold)" }}>
        <h1 className="section-title" style={{ fontSize:"clamp(3rem,8vw,6rem)" }}>VALORES MILITARES</h1>
        <div className="section-line" style={{ margin:"0 auto" }}/>
        <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:"1rem", letterSpacing:2, color:"var(--sand)", maxWidth:500, margin:"0 auto", textTransform:"uppercase" }}>
          Los principios que guían cada decisión y cada acción de nuestros soldados
        </p>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"5rem 2rem" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:"1.4rem" }}>
          {valores.map((v, i) => (
            <div
              key={v.name} className="reveal"
              style={{
                background:"var(--dark-olive)", border:"1px solid rgba(200,168,75,.15)",
                padding:"2.2rem 1.8rem", position:"relative", overflow:"hidden",
                transition:"all .3s", animationDelay:`${i * 0.1}s`,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform="translateY(-5px)"; e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.boxShadow="0 12px 40px rgba(0,0,0,.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.borderColor="rgba(200,168,75,.15)"; e.currentTarget.style.boxShadow="none"; }}
            >
              <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"var(--gold)", transform:"scaleX(0)", transformOrigin:"left", transition:"transform .3s" }}
                onMouseEnter={e => e.currentTarget.style.transform="scaleX(1)"}
              />
             {v.img 
               ? <img src={v.img} alt={v.name} style={{ width:"2.6rem", height:"2.6rem", marginBottom:"1rem", display:"block" }} />
                  : <i className={v.icon} style={{ fontSize:"2.6rem", color:"var(--gold)", marginBottom:"1rem", display:"block" }} />
                }
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.4rem", letterSpacing:3, color:"var(--gold)", marginBottom:".6rem" }}>{v.name}</div>
              <div style={{ fontSize:".8rem", lineHeight:1.75, textAlign:"justify", color:"var(--sand)" }}>{v.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: FUERZAS
// ═══════════════════════════════════════════════════════════════════════════

function PageFuerzas() {
  useReveal();
  const [active, setActive] = useState(0);

  const fuerzas = [
    {
      icon: "/logos/Log-MaGue.png",
      name: "Batallón de Material de Guerra N° 111",
      subtitle: 'SLDO ALVARADO RODRÍGUEZ GASPAR" N° 111',
      color: "#4a7c59",
      desc: "Unidad encargada del abastecimiento, almacenamiento y distribución de municiones y material bélico a las unidades de la I DE.",
      capacidades: [
        "Abastecimiento",
        "Almacén",
        "Inventario",
        "Distribución en zona de operaciones",
      ],
      efectivos: "Batallón A/M",
    },
    {
      icon: "/logos/Log-int.png",
      name: "Batallón de Intendencia N° 111",
      subtitle: '"MY FERNANDO GARCÍA MURGADO" N° 111',
      color: "#2d6080",
      desc: "Responsable del abastecimiento de víveres, vestuario y equipos de campaña para el personal militar.",
      capacidades: [
        "Abastecimiento",
        "Gestión de vestuario militar",
        "Cocinas de campaña",
        "Apoyo logístico en maniobras",
      ],
      efectivos: "Batallón A/M",
    },
    {
      icon: "/logos/Log-Trans.png",
      name: "Batallón de Transportes N° 111",
      subtitle: 'CAP JIMMY RODRÍGUEZ CRUZ" N° 111',
      color: "#1a3a5c",
      desc: "Unidad motorizada que garantiza el movimiento de tropas, material y equipos a lo largo del área de responsabilidad de la brigada.",
      capacidades: [
        "Transporte de personal",
        "Movimiento de carga pesada",
        "Apoyo a evacuación",
        "Operaciones de convoy",
      ],
      efectivos: "Batallón",
    },
    {
      icon: "/logos/Log-Ing.png",
      name: "Compañía de Ingeniería de Servicios N° 111",
      subtitle: "",
      color: "#7a3d2e",
      desc: "Proporciona apoyo técnico de ingeniería para el mantenimiento de infraestructura, instalaciones y equipos en el área de operaciones.",
      capacidades: [
        "Mantenimiento de instalaciones",
        "Construcción",
        "Apoyo de ingeniería",
        "Reparación de infraestructura",
      ],
      efectivos: "Compañía",
    },
    {
      icon: "/logos/Logo-Com.png",
      name: "Compañía de Comunicaciones de Servicios N° 111",
      subtitle: "",
      color: "#3a4a2e",
      desc: "Unidad especializada en establecer y mantener los sistemas de comunicación táctica y estratégica.",
      capacidades: [
        "Redes de comunicación",
        "Operación de radio frecuencia",
        "Enlace entre unidades",
        "Comunicaciones cifradas",
      ],
      efectivos: "Compañía",
    },
    {
      icon: "/logos/Log-San.png",
      name: "Compañía de Sanidad Veterinaria N° 111",
      subtitle: "",
      color: "#5a3a1a",
      desc: "Brinda atención médica de emergencia y control sanitario en operaciones.",
      capacidades: [
        "Atención médica de campaña",
        "Evacuación de heridos",
        "Control sanitario de alimentos",
        "Salud veterinaria operacional",
      ],
      efectivos: "Compañía",
    },
    {
      icon: "/logos/Log-Coman.png",
      name: "Compañía Comando y Servicios N° 111",
      subtitle: "",
      color: "#2a3a5a",
      desc: "Elemento de mando y coordinación de la brigada.",
      capacidades: [
        "Planificación y mando",
        "Coordinación administrativa",
        "Seguridad del comando",
        "Gestión de operaciones logísticas",
      ],
      efectivos: "Compañía",
    },
  ];

  return (
    <div style={{ paddingTop: 62 }}>
      {/* HEADER */}
      <div
        style={{
          background: "linear-gradient(135deg,#0d110a,#1e2a17)",
          padding: "5rem 2rem 4rem",
          textAlign: "center",
          borderBottom: "2px solid var(--gold)",
        }}
      >
        <h1 className="section-title" style={{ fontSize: "clamp(3rem,8vw,6rem)" }}>
          RAMAS Y FUERZAS
        </h1>
        <div className="section-line" style={{ margin: "0 auto" }} />
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "5rem 2rem" }}>
        
        {/* TABS */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "3rem" }}>
          {fuerzas.map((f, i) => (
            <button
              key={f.name}
              onClick={() => setActive(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: ".5rem",
                padding: ".6rem 1.4rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: ".82rem",
                letterSpacing: "2px",
                
                textTransform: "uppercase",
                cursor: "pointer",
                border: "1px solid",
                borderColor: active === i ? "var(--gold)" : "rgba(200,168,75,.2)",
                background: active === i ? "rgba(200,168,75,.12)" : "transparent",
                color: active === i ? "var(--gold)" : "var(--sand)",
                transition: "all .2s",
              }}
            >
              <img
                src={f.icon}
                alt=""
                style={{ width: 18, height: 18, objectFit: "contain" }}
                onError={(e) => (e.target.style.display = "none")}
              />
              {f.name}
            </button>
          ))}
        </div>

        {/* PANEL ACTIVO */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", alignItems: "start" }}>
          
          {/* IZQUIERDA */}
          <div className="reveal">
          <div
            style={{
              width: 270,
              height: 270,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1rem",
              margin: "0 auto 1rem auto",
            }}
          >
            <img
              src={fuerzas[active].icon}
              alt={fuerzas[active].name}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.innerHTML =
                  "<i class='bx bx-shield' style='font-size:5rem;color:var(--gold)'></i>";
              }}
            />
          </div>

            <h2
              style={{
                fontFamily: "'Bebas Neue',sans-serif",
                textAlign: "center",
                fontSize: "2.5rem",
                letterSpacing: 3,
                color: "var(--gold)",
                marginBottom: "1rem",
              }}
            >
              {fuerzas[active].name}
            </h2>

            <p style={{ fontSize: ".97rem", lineHeight: 1.8, color: "var(--sand)", marginBottom: "1.5rem" , textAlign:"center"}}>
              {fuerzas[active].desc}
            </p>
          </div>

          {/* DERECHA */}
          <div className="reveal">
            <h3
              style={{
                fontFamily: "'Bebas Neue',sans-serif",
                fontSize: "1.4rem",
                letterSpacing: 3,
                color: "var(--cream)",
                marginBottom: "1.2rem",
              }}
            >
              CAPACIDADES PRINCIPALES
            </h3>

            {fuerzas[active].capacidades.map((c) => (
              <div
                key={c}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: ".9rem 1.2rem",
                  marginBottom: ".6rem",
                  background: "var(--dark-olive)",
                  borderLeft: "3px solid var(--gold)",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: ".95rem",
                  letterSpacing: 1,
                  color: "var(--sand)",
                }}
              >
                <span style={{ color: "var(--gold)" }}>▸</span> {c}
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: HISTORIA
// ═══════════════════════════════════════════════════════════════════════════
function PageHistoria() {
  useReveal();

  const eventos = [
    {
      year: "1941",
      title: "Bautismo de Fuego — Conflicto con Ecuador",
      desc: "El Batallón de Servicios participa activamente en la Campaña Militar del Norte, brindando apoyo logístico al Agrupamiento del Norte bajo el mando del General Eloy Ureta. En esta campaña cae heroicamente el SLDO Isaac Rodríguez Mendoza, quien se enfrenta valientemente al enemigo en la frontera.",
      tag: "Conflicto",
      img: "/historia/1941.jpg",
    },
    {
      year: "1942",
      title: "Protocolo de Río de Janeiro",
      desc: "Tras la victoria peruana en la campaña del norte, se firma el Protocolo de Paz de Río de Janeiro. El Batallón de Servicios regresa a su guarnición en Piura habiendo demostrado la importancia del apoyo logístico en operaciones de combate.",
      tag: "Historia",
      img: "/historia/1942.png",
    },
    {
      year: "1981",
      title: "Conflicto del Falso Paquisha",
      desc: "El Batallón de Servicios es activado nuevamente ante las tensiones fronterizas con Ecuador en el denominado conflicto del 'Falso Paquisha', prestando apoyo logístico a las unidades de combate desplegadas en la zona norte del país.",
      tag: "Conflicto",
      img: "/historia/1981.png",
    },
    {
      year: "1988",
      title: "Nueva Denominación — Destacamento de Servicios N° 111",
      desc: "La unidad es reorganizada y recibe la denominación oficial de Destacamento de Servicios N° 111, consolidando su estructura logística de apoyo a la I División del Ejército con sede en Piura.",
      tag: "Reorganización",
      img: "/historia/1988.jpg",
    },
    {
      year: "1995",
      title: "Guerra del Cenepa",
      desc: "El Destacamento de Servicios N° 111 participa en el esfuerzo logístico nacional durante el conflicto del Alto Cenepa con Ecuador, garantizando el abastecimiento de clases I, III y V a las unidades combatientes desplegadas en la frontera norte.",
      tag: "Conflicto",
      img: "/historia/1995.jpg",
    },
    {
      year: "2004",
      title: "Fin del Destacamento — Transición Institucional",
      desc: "Culmina la etapa del Destacamento de Servicios N° 111 tras 16 años de operaciones. La unidad inicia el proceso de reorganización que la convertirá en una Gran Unidad de mayor envergadura.",
      tag: "Reorganización",
      img: "/historia/2004.jpg",
    },
    {
      year: "2005",
      title: "Brigada de Servicios N° 111",
      desc: "La unidad asciende de categoría y pasa a denominarse Brigada de Servicios N° 111, ampliando sus capacidades logísticas y su área de responsabilidad desde Tumbes hasta Trujillo, en apoyo a toda la I División del Ejército.",
      tag: "Ascenso",
      img: "/historia/2005.jpg",
    },
    {
      year: "2011",
      title: "1ra Brigada de Servicios — Decreto Supremo",
      desc: "Mediante Decreto Supremo del 07 de octubre de 2011, la unidad recibe su denominación definitiva como 1ra Brigada de Servicios, acantonada en el Cuartel SLDO 'Isaac Rodríguez Mendoza' en El Chipe, Piura, en honor al héroe caído en 1941.",
      tag: "Fundación",
      img: "/historia/2011.jpeg",
    },
    {
      year: "2017",
      title: "Apoyo ante Fenómeno El Niño Costero",
      desc: "La 1ra Brigada de Servicios despliega sus unidades para apoyar a la población piurana afectada por el Fenómeno El Niño Costero, una de las emergencias más graves de la región norte, demostrando su rol en el SINAGERD.",
      tag: "Humanitario",
      img: "/historia/2017.jpg",
    },
    {
      year: "2025",
      title: "Brigada del Siglo XXI",
      desc: "Bajo el mando del Coronel Roberto Fabio Pozo Palomino, la 1ra Brigada de Servicios moderniza sus capacidades logísticas, incorporando nuevos sistemas de gestión y mantenimiento para seguir siendo el pilar de apoyo de la I División del Ejército.",
      tag: "Modernización",
      img: "/historia/1.jpg",
    },
  ];

  const tagColors = {
    Conflicto:      "#8b1c1c",
    Historia:       "#2d6080",
    Reorganización: "#4a5a2e",
    Ascenso:        "#c8a84b",
    Fundación:      "#3a5a8a",
    Humanitario:    "#3a6a4a",
    Modernización:  "#5a3a8a",
  };

  const tagIcons = {
    Conflicto:      "bx bxs-shield-x",
    Historia:       "bx bx-book",
    Reorganización: "bx bx-transfer",
    Ascenso:        "bx bxs-up-arrow-circle",
    Fundación:      "bx bxs-flag",
    Humanitario:    "bx bxs-heart",
    Modernización:  "bx bx-chip",
  };

  return (
    <div style={{ paddingTop: 62 }}>

      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#0d110a,#1e2a17)",
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: "2px solid var(--gold)",
      }}>
        <div className="section-tag" style={{ textAlign: "center" }}>Página — Historia</div>
        <h1 className="section-title" style={{ fontSize: "clamp(3rem,8vw,6rem)" }}>HISTORIA MILITAR</h1>
        <div className="section-line" style={{ margin: "0 auto" }} />
        <p style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: "1rem",
          letterSpacing: 2, color: "var(--sand)", maxWidth: 500,
          margin: "0 auto", textTransform: "uppercase",
        }}>
          Más de 80 años al servicio de la patria
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "5rem 2rem" }}>
        <div style={{ position: "relative" }}>

          {/* Línea vertical central */}
          <div style={{
            position: "absolute", left: "50%", top: 0, bottom: 0,
            width: 2, transform: "translateX(-50%)",
            background: "linear-gradient(to bottom, var(--gold), rgba(200,168,75,.08))",
          }} />

          {eventos.map((e, i) => {
            const isLeft = i % 2 === 0;   // alterna izquierda/derecha
            return (
              <div
                key={e.year}
                className="reveal"
                style={{
                  display: "flex",
                  justifyContent: isLeft ? "flex-start" : "flex-end",
                  marginBottom: "3rem",
                  position: "relative",
                }}
              >
                {/* Año centrado sobre la línea */}
                <div style={{
                  position: "absolute", left: "50%", top: "1.2rem",
                  transform: "translateX(-50%)",
                  zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                }}>
                  <div style={{
                    width: 16, height: 16, background: "var(--gold)",
                    borderRadius: "50%", border: "3px solid var(--charcoal)",
                    boxShadow: "0 0 14px rgba(200,168,75,.6)",
                  }} />
                  <div style={{
                    background: "var(--gold)", color: "var(--charcoal)",
                    fontFamily: "'Bebas Neue',sans-serif", fontSize: ".85rem",
                    letterSpacing: 2, padding: ".1rem .5rem", whiteSpace: "nowrap",
                  }}>
                    {e.year}
                  </div>
                </div>

                {/* Tarjeta — ocupa 45% del ancho */}
                <div style={{
                  width: "45%",
                  background: "var(--dark-olive)",
                  border: "1px solid rgba(200,168,75,.15)",
                  overflow: "hidden",
                  transition: "all .3s",
                  boxShadow: "0 4px 20px rgba(0,0,0,.3)",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(0,0,0,.5)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(200,168,75,.15)"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.3)"; }}
                >
                  {/* Imagen */}
                  <div style={{ height: 180, overflow: "hidden", background: "#111409", position: "relative" }}>
                    <img
                      src={e.img}
                      alt={e.title}
                      style={{
                        width: "100%", height: "100%", objectFit: "cover",
                        transition: "transform .5s ease",
                        filter: "brightness(0.85) sepia(0.2)",
                      }}
                      onMouseEnter={ev => ev.currentTarget.style.transform = "scale(1.06)"}
                      onMouseLeave={ev => ev.currentTarget.style.transform = "scale(1)"}
                      onError={ev => {
                        ev.target.style.display = "none";
                        ev.target.parentNode.style.display = "flex";
                        ev.target.parentNode.style.alignItems = "center";
                        ev.target.parentNode.style.justifyContent = "center";
                        ev.target.parentNode.innerHTML = `<i class='${tagIcons[e.tag] || "bx bx-image"}' style='font-size:3.5rem;color:rgba(200,168,75,.2)'></i>`;
                      }}
                    />
                    {/* Overlay degradado */}
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0, height: "50%",
                      background: "linear-gradient(to top, rgba(10,14,8,.9), transparent)",
                    }} />
                    {/* Tag encima de la imagen */}
                    <div style={{
                      position: "absolute", bottom: ".8rem", left: ".9rem",
                      display: "flex", alignItems: "center", gap: ".4rem",
                      background: tagColors[e.tag] || "#333",
                      padding: ".2rem .7rem",
                      fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".65rem",
                      letterSpacing: "2px", textTransform: "uppercase", color: "white",
                    }}>
                      <i className={tagIcons[e.tag] || "bx bx-info-circle"} style={{ fontSize: ".85rem" }}></i>
                      {e.tag}
                    </div>
                  </div>

                  {/* Texto */}
                  <div style={{ padding: "1.3rem 1.5rem" }}>
                    <h3 style={{
                      fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.15rem",
                      letterSpacing: 2, color: "var(--cream)", marginBottom: ".6rem", lineHeight: 1.2,
                    }}>
                      {e.title}
                    </h3>
                    <p style={{
                      fontSize: ".86rem", lineHeight: 1.75,
                      color: "var(--sand)", textAlign: "justify",
                    }}>
                      {e.desc}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive: en móvil todo a la izquierda */}
      <style>{`
        @media (max-width: 700px) {
          .timeline-card { width: 85% !important; margin-left: 10% !important; }
        }
      `}</style>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// PAGE: EQUIPAMIENTO
// ═══════════════════════════════════════════════════════════════════════════
function PageEquipamiento() {
  useReveal();
  const categorias = [
    {
      cat:"Armamento Personal", items:[
        { name:"Fusil de Asalto M4A1", esp:"Cal. 5.56mm · 800 rpm · 500m efectivo", desc:"Fusil estándar de infantería ligero, modular y de alta fiabilidad en condiciones extremas." },
        { name:"Pistola Glock 17", esp:"Cal. 9mm · 17+1 rondas · 50m efectivo", desc:"Arma de dotación para oficiales. Reconocida por su confiabilidad y facilidad de uso." },
        { name:"Francotirador Barrett M82", esp:"Cal. .50 BMG · 1800m efectivo", desc:"Sistema de largo alcance para neutralización de objetivos a distancia extrema." },
      ]
    },
    {
      cat:"Vehículos y Blindados", items:[
        { name:"Vehículo MRAP MaxxPro", esp:"4x4 · Blindaje V-hull · 10 plazas", desc:"Protección contra minas e IEDs. Estándar para operaciones en zonas de conflicto activo." },
        { name:"Tanque Leopard 2A6", esp:"120mm · 67 ton · 72 km/h", desc:"Carro de combate principal de última generación con protección activa e integración digital." },
        { name:"APC M113A3", esp:"Anfibio · 12.7mm · 11 plazas", desc:"Transporte blindado de personal para movimiento seguro en zonas hostiles." },
      ]
    },
    {
      cat:"Aviación", items:[
        { name:"Helicóptero Black Hawk UH-60", esp:"11 soldados · 294 km/h · 600km", desc:"Transporte táctico versátil. Empleado en evacuación médica, asalto aéreo y logística." },
        { name:"Dron RQ-7 Shadow", esp:"Autonomía 6h · 125km · EO/IR", desc:"Sistema UAV de reconocimiento táctico para inteligencia en tiempo real sin riesgo tripulado." },
        { name:"Cazabombardero F-16C", esp:"Mach 2.0 · 9G · Multimisión", desc:"Caza polivalente para superioridad aérea, interdicción y apoyo a tropas terrestres." },
      ]
    },
    {
      cat:"Tecnología y Comunicaciones", items:[
        { name:"Radio Táctica AN/PRC-158", esp:"Cifrado · Multi-banda · 30km", desc:"Sistema de comunicaciones seguro para coordinación en el campo de batalla en tiempo real." },
        { name:"Sistema C2 Digital ABMS", esp:"Red · IA · Tiempo real", desc:"Plataforma de mando y control que integra sensores, datos y unidades en un solo sistema." },
        { name:"Chaleco Exoesqueleto TALOS", esp:"Asistencia motriz · Blindaje · Sensores", desc:"Traje de combate aumentado que incrementa la resistencia y capacidades del soldado." },
      ]
    },
  ];

  return (
    <div style={{ paddingTop:62 }}>
      <div style={{ background:"linear-gradient(135deg,#0d110a,#1e2a17)", padding:"5rem 2rem 4rem", textAlign:"center", borderBottom:"2px solid var(--gold)" }}>
        <h1 className="section-title" style={{ fontSize:"clamp(3rem,8vw,6rem)" }}>EQUIPAMIENTO</h1>
        <div className="section-line" style={{ margin:"0 auto" }}/>
        <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:"1rem", letterSpacing:2, color:"var(--sand)", maxWidth:500, margin:"0 auto", textTransform:"uppercase" }}>
          Tecnología y armamento de última generación al servicio de la nación
        </p>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"5rem 2rem" }}>
        {categorias.map((cat) => (
          <div key={cat.cat} style={{ marginBottom:"4rem" }}>
            <div className="reveal" style={{ display:"flex", alignItems:"center", gap:"1rem", marginBottom:"1.5rem" }}>
              <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.8rem", letterSpacing:3, color:"var(--gold)" }}>{cat.cat}</h2>
              <div style={{ flex:1, height:1, background:"rgba(200,168,75,.2)" }}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"1rem" }}>
              {cat.items.map((item) => (
                <div key={item.name} className="reveal" style={{
                  background:"var(--dark-olive)", border:"1px solid rgba(200,168,75,.12)",
                  padding:"1.6rem",
                  transition:"all .3s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.transform="translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(200,168,75,.12)"; e.currentTarget.style.transform="none"; }}
                >
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.2rem", letterSpacing:2, color:"var(--cream)", marginBottom:".3rem" }}>{item.name}</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".72rem", letterSpacing:"2px", color:"var(--gold)", marginBottom:".8rem", textTransform:"uppercase" }}>{item.esp}</div>
                  <div style={{ fontSize:".87rem", lineHeight:1.7, color:"var(--sand)" }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: NOTICIAS
// ═══════════════════════════════════════════════════════════════════════════
function PageNoticias() {
  useReveal();

const noticias = [
  {
    cat: "Formación",
    fecha: "9 Mar 2026",
    titulo: "Clausura del Curso de Sistemas de Lubricación",
    desc: "La actividad fue presidida por el señor Coronel de Intendencia, Comandante General de la Primera Brigada de Servicios. Este curso permitió a los participantes ampliar sus conocimientos en el funcionamiento y mantenimiento de los sistemas de lubricación en motores diésel.",
    img: "/noticias/diesel.jpg",
    link: "https://www.facebook.com/share/p/1B4YdQyYwg/",
    destacada: true,  // ← ascendida a destacada por ser la más reciente
  },
  {
    cat: "Reconocimiento",
    fecha: "07 Feb 2026",
    titulo: "Reconocimiento de los comandantes de unidad",
    desc: "La Primera Brigada de Servicios realizó una ceremonia de reconocimiento a los nuevos comandantes de unidad, quienes asumirán importantes responsabilidades dentro de la institución.",
    img: "/noticias/reconocimiento.jpg",
    link: "https://www.facebook.com/share/p/1KnmVYJpbY/",
  },
  {
    cat: "Ayuda",
    fecha: "6 Feb 2026",
    titulo: "Donación de Sangre a favor de la comunidad",
    desc: "El personal militar participó en una jornada de donación de sangre destinada a apoyar a la Congregación Hermanitas de los Ancianos Desamparados, reflejando el compromiso del Ejército del Perú con la comunidad.",
    img: "/noticias/donacion2.jpg",
    link: "https://www.facebook.com/share/p/1JD9j9LBVw/",
  },
  {
    cat: "Actualidad",
    fecha: "5 Feb 2026",
    titulo: "Vacante en el proceso de admisión",
    desc: "La Primera Brigada de Servicios expresó su reconocimiento al personal de reenganchados que obtuvo una vacante en el Instituto de Educación Superior Tecnológico Público del Ejército.",
    img: "/noticias/ingreso.jpg",
    link: "https://www.facebook.com/share/p/1CfmoA9X1a/",
  },
  {
    cat: "Ayuda",
    fecha: "21 Ene 2026",
    titulo: "Campaña de Vacunación contra el Dengue",
    desc: "La Primera Brigada de Servicios, a través de la Compañía de Sanidad Veterinaria N.º111, llevó a cabo una Campaña de Vacunación contra el Dengue en beneficio del personal militar.",
    img: "/noticias/vacunacion.jpg",
    link: "https://www.facebook.com/permalink.php?story_fbid=pfbid0WbsCFU3tEHgCLy16jtzhcbBWcbDNJMoqKwtwm9HoxB19dRtNvhotMbRQcBcTzT3Yl&id=61573002132471",
  },
  {
    cat: "Provincial",
    fecha: "28 Feb 2025",
    titulo: "Izamiento del Pabellón Nacional en El Arenal",
    desc: "La Primera Brigada de Servicios, a través del Batallón de Intendencia N.º 111, participó en la ceremonia de izamiento del Pabellón Nacional y en una campaña médica realizada en el distrito de El Arenal, en la provincia de Paita.",
    img: "/noticias/izamiento.jpg",
    link: "https://www.facebook.com/share/p/1Kg8pDWe8M/",
  },
];

  const catColors = {
    Reconocimiento: "#8b1c1c",
    Actualidad:     "#2d6080",
    Formación:      "#4a5a2e",
    Provincial:     "#1a3a5c",
    Ayuda:          "#3a5a2e",
    Tecnología:     "#2a4a3a",
  };

  return (
    <div style={{ paddingTop: 62 }}>
      <div style={{
        background: "linear-gradient(135deg,#0d110a,#1e2a17)",
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: "2px solid var(--gold)",
      }}>
        <div className="section-tag" style={{ textAlign: "center" }}>Página — Noticias</div>
        <h1 className="section-title" style={{ fontSize: "clamp(3rem,8vw,6rem)" }}>NOTICIAS Y COMUNICADOS</h1>
        <div className="section-line" style={{ margin: "0 auto" }} />
      <button
    onClick={() => window.open("https://online.fliphtml5.com/Revista1Brigada/LA-PRIMERA-3EzA/", "_blank")}
    style={{
      marginTop: "1.5rem",
      background: "transparent",
      border: "1px solid var(--gold)",
      color: "var(--gold)",
      fontFamily: "'Barlow Condensed',sans-serif",
      fontSize: ".88rem", letterSpacing: "3px", textTransform: "uppercase",
      padding: ".75rem 2rem", cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: ".5rem",
      transition: "all .25s",
    }}
    onMouseEnter={e => { e.currentTarget.style.background = "rgba(200,168,75,.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.transform = "none"; }}
  >
    <i className="bx bx-book-open" style={{ fontSize: "1.1rem" }}></i>
    Ver Revista Digital
    <i className="bx bx-link-external" style={{ fontSize: ".85rem" }}></i>
  </button>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "5rem 2rem" }}>

        {/* DESTACADA */}
        <div
          className="reveal"
          onClick={() => window.open(noticias[0].link, "_blank")}
          style={{
            background: "var(--dark-olive)", border: "1px solid var(--gold)",
            marginBottom: "2rem", position: "relative",
            cursor: "pointer", transition: "all .3s",
            display: "grid", gridTemplateColumns: "1fr 1fr",
            overflow: "hidden",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,.4)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
        >
          {/* Imagen lado izquierdo */}
          <div style={{ height: 280, overflow: "hidden", background: "#1a1e17", position: "relative" }}>
            <img
              src={noticias[0].img}
              alt={noticias[0].titulo}
              style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .4s" }}
              onError={e => {
                e.target.parentNode.style.display = "flex";
                e.target.parentNode.style.alignItems = "center";
                e.target.parentNode.style.justifyContent = "center";
                e.target.style.display = "none";
                e.target.parentNode.innerHTML = `<i class='bx bx-image' style='font-size:4rem;color:rgba(200,168,75,.2)'></i>`;
              }}
            />
            <div style={{
              position: "absolute", top: "1rem", left: "1rem",
              background: "var(--gold)", padding: ".2rem .8rem",
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".7rem",
              letterSpacing: "3px", textTransform: "uppercase",
              color: "var(--charcoal)", fontWeight: 700,
            }}>
              DESTACADO
            </div>
          </div>

          {/* Texto lado derecho */}
          <div style={{ padding: "2rem 2.5rem", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", gap: ".8rem", alignItems: "center", marginBottom: ".8rem" }}>
              <span style={{
                background: catColors[noticias[0].cat], padding: ".2rem .7rem",
                fontSize: ".7rem", letterSpacing: "2px",
                fontFamily: "'Barlow Condensed',sans-serif",
                textTransform: "uppercase", color: "white",
              }}>
                {noticias[0].cat}
              </span>
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".75rem", color: "var(--muted)", letterSpacing: 1 }}>
                {noticias[0].fecha}
              </span>
            </div>
            <h2 style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: "clamp(1.4rem,2.5vw,2rem)",
              letterSpacing: 2, color: "var(--cream)",
              marginBottom: ".8rem", lineHeight: 1.2,
            }}>
              {noticias[0].titulo}
            </h2>
            <p style={{ fontSize: ".9rem", lineHeight: 1.75, color: "var(--sand)", textAlign: "justify" }}>
              {noticias[0].desc}
            </p>
            <div style={{
              marginTop: "1.2rem", display: "flex", alignItems: "center", gap: ".4rem",
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".78rem",
              letterSpacing: "2px", color: "var(--gold)", textTransform: "uppercase",
            }}>
              <i className="bx bx-news" style={{ fontSize: "1.1rem" }}></i>
              Ver en Noticia →
            </div>
          </div>
        </div>

        {/* GRID NOTICIAS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: "1.2rem" }}>
          {noticias.slice(1).map((n) => (
            <div
              key={n.titulo}
              className="reveal"
              onClick={() => window.open(n.link, "_blank")}
              style={{
                background: "var(--dark-olive)", border: "1px solid rgba(200,168,75,.12)",
                transition: "all .3s", cursor: "pointer", overflow: "hidden",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "var(--gold)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,.35)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "rgba(200,168,75,.12)";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Imagen */}
              <div style={{ height: 170, overflow: "hidden", background: "#1a1e17", position: "relative" }}>
                <img
                  src={n.img}
                  alt={n.titulo}
                  style={{ width: "100%", height: "140%", objectFit: "cover", transition: "transform .4s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                  onError={e => {
                    e.target.parentNode.style.display = "flex";
                    e.target.parentNode.style.alignItems = "center";
                    e.target.parentNode.style.justifyContent = "center";
                    e.target.style.display = "none";
                    e.target.parentNode.innerHTML = `<i class='bx bx-image' style='font-size:3rem;color:rgba(200,168,75,.15)'></i>`;
                  }}
                />
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
                  background: "linear-gradient(to top,rgba(10,14,8,.8),transparent)",
                }} />
              </div>

              {/* Contenido texto */}
              <div style={{ padding: "1.3rem 1.4rem" }}>
                <div style={{ display: "flex", gap: ".6rem", alignItems: "center", marginBottom: ".7rem" }}>
                  <span style={{
                    background: catColors[n.cat] || "#333", padding: ".15rem .6rem",
                    fontSize: ".65rem", letterSpacing: "2px",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    textTransform: "uppercase", color: "white",
                  }}>
                    {n.cat}
                  </span>
                  <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".72rem", color: "var(--muted)" }}>
                    {n.fecha}
                  </span>
                </div>

                <h3 style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontSize: "1.05rem",
                  letterSpacing: 1, color: "var(--cream)", marginBottom: ".6rem",
                  lineHeight: 1.4, fontWeight: 700,
                }}>
                  {n.titulo}
                </h3>

                <p style={{ fontSize: ".85rem", lineHeight: 1.65, color: "var(--sand)", textAlign: "justify" }}>
                  {n.desc}
                </p>

                {/* Facebook link */}
                <div style={{
                  marginTop: "1rem", display: "flex", alignItems: "center", gap: ".4rem",
                  fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".75rem",
                  letterSpacing: "2px", color: "var(--gold)", textTransform: "uppercase",
                }}>
                  <i className="bx bx-news" style={{ fontSize: "1rem" }}></i>
                  Ver en Noticia →
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ENLISTARSE
// ═══════════════════════════════════════════════════════════════════════════
function PageEnlistarse() {
  useReveal();
  const [form, setForm] = useState({ nombre:"", email:"", telefono:"", rama:"", mensaje:"" });
  const [sent, setSent] = useState(false);

  const WHATSAPP_NUMBER = "51954781135"

  const input = {
    background:"rgba(19,22,15,0.8)", border:"1px solid rgba(200,168,75,.25)",
    color:"var(--cream)", padding:".85rem 1.2rem", fontFamily:"'Barlow',sans-serif",
    fontSize:".93rem", outline:"none", width:"100%", transition:"border .2s",
    marginBottom:"1rem",
  };

  const handleSubmit = () => {
    if (!form.nombre || !form.email) {
      alert("Por favor completa al menos el nombre y el correo electrónico.");
      return;
    }

    const mensaje = 
`*NUEVA SOLICITUD DE ENLISTAMIENTO*
*1ra Brigada de Servicios — Piura*

*Nombre:* ${form.nombre}
*Correo:* ${form.email}
*Teléfono:* ${form.telefono || "No especificado"}
*Unidad de interés:* ${form.rama || "No especificada"}
*Mensaje:* ${form.mensaje || "Sin mensaje adicional"}

_Enviado desde el sitio web oficial._`;

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensaje)}`;
    window.open(url, "_blank");

    setSent(true);

    setForm({ nombre:"", email:"", telefono:"", rama:"", mensaje:"" });

    setTimeout(() => setSent(false), 5000);
  };

  return (
    <div style={{ paddingTop:62 }}>
      <div style={{ background:"linear-gradient(135deg,#0d110a,#1e2a17)", padding:"5rem 2rem 4rem", textAlign:"center", borderBottom:"2px solid var(--gold)" }}>
        <h1 className="section-title" style={{ fontSize:"clamp(3rem,8vw,6rem)" }}>ÚNETE A LAS FILAS</h1>
        <div className="section-line" style={{ margin:"0 auto" }}/>
        <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:"1rem", letterSpacing:2, color:"var(--sand)", maxWidth:500, margin:"0 auto", textTransform:"uppercase" }}>
          ¿Estás listo para asumir el reto más grande de tu vida?
        </p>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"5rem 2rem" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4rem", alignItems:"start" }}>

          {/* Requisitos */}
          <div className="reveal">
            <div className="section-tag">Requisitos</div>
            <h2 className="section-title">PARA ENLISTARTE</h2>
            <div className="section-line"/>
            {[
              { icon:"bx bx-id-card",       req:"DNI vigente (original y copia)" },
              { icon:"bx bx-calendar",      req:"Tener entre 18 y 25 años de edad" },
              { icon:"bx bx-notepad",       req:"Constancia de Inscripción Militar (calificación SELECCIONADO)" },
              { icon:"bx bx-book",          req:"Certificado de estudios mínimo secundaria completa" },
              { icon:"bx bx-shield-x",      req:"Sin antecedentes policiales, penales ni judiciales" },
              { icon:"bx bx-heart",         req:"Aprobar examen de aptitud psicosomática" },
              { icon:"bx bx-run",           req:"Buenas condiciones físicas y de salud comprobadas" },
              { icon:"bx bx-map-pin",       req:"Presentar documentos en el Cuartel Isaac Rodríguez de Mendoza — El Chipe, Piura" },
            ].map((r) => (
              <div key={r.req} style={{ display:"flex", alignItems:"center", gap:"1rem", padding:".9rem 1.2rem", marginBottom:".5rem", background:"var(--dark-olive)", borderLeft:"3px solid var(--gold)" }}>
                <i className={r.icon} style={{ fontSize:"1.3rem", color:"var(--gold)", minWidth:22 }}></i>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".95rem", letterSpacing:1, color:"var(--sand)" }}>{r.req}</span>
              </div>
            ))}

            <div style={{ marginTop:"2rem", padding:"1.4rem", background:"rgba(200,168,75,.06)", border:"1px solid rgba(200,168,75,.2)" }}>
              <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".85rem", letterSpacing:1, color:"var(--sand)", lineHeight:1.9 }}>
                📞 Centro de Reclutamiento: <span style={{ color:"var(--gold)" }}>+51 954 781 135</span><br/>
                📧 Email: <span style={{ color:"var(--gold)" }}>Jhonnycastro49@gmail.com</span><br/>
                🕐 Lunes — Viernes 08:00 Am – 3:00 Pm
              </p>
            </div>
          </div>

          {/* Formulario */}
          <div className="reveal">
            <div className="section-tag">Formulario</div>
            <h2 className="section-title">SOLICITAR INFORMACIÓN</h2>
            <div className="section-line"/>

            {/* Mensaje de éxito */}
            {sent && (
              <div style={{
                background:"rgba(74,122,89,.25)", border:"1px solid #4a7a59",
                padding:"1rem 1.4rem", marginBottom:"1.5rem",
                fontFamily:"'Barlow Condensed',sans-serif", fontSize:".9rem",
                letterSpacing:2, color:"#8dcca0",
                display:"flex", alignItems:"center", gap:".6rem",
              }}>
                <i className="bx bx-check-circle" style={{ fontSize:"1.4rem" }}></i>
                SOLICITUD ENVIADA — REDIRIGIENDO A WHATSAPP
              </div>
            )}

            <input
              style={input}
              placeholder="Nombre completo *"
              value={form.nombre}
              onChange={e => setForm({...form, nombre:e.target.value})}
              onFocus={e => e.target.style.borderColor="var(--gold)"}
              onBlur={e => e.target.style.borderColor="rgba(200,168,75,.25)"}
            />
            <input
              style={input}
              placeholder="Correo electrónico *"
              value={form.email}
              onChange={e => setForm({...form, email:e.target.value})}
              onFocus={e => e.target.style.borderColor="var(--gold)"}
              onBlur={e => e.target.style.borderColor="rgba(200,168,75,.25)"}
            />
            <input
              style={input}
              placeholder="Teléfono de contacto"
              value={form.telefono}
              onChange={e => setForm({...form, telefono:e.target.value})}
              onFocus={e => e.target.style.borderColor="var(--gold)"}
              onBlur={e => e.target.style.borderColor="rgba(200,168,75,.25)"}
            />
            <select
              style={{ ...input, cursor:"pointer" }}
              value={form.rama}
              onChange={e => setForm({...form, rama:e.target.value})}
              onFocus={e => e.target.style.borderColor="var(--gold)"}
              onBlur={e => e.target.style.borderColor="rgba(200,168,75,.25)"}
            >
              <option value="">Selecciona una unidad de interés</option>
              <option>Batallón de Material de Guerra A/M N° 111</option>
              <option>Batallón de Intendencia A/M N° 111</option>
              <option>Batallón de Transportes N° 111</option>
              <option>Compañía de Ingeniería de Servicios N° 111</option>
              <option>Compañía de Comunicaciones y Servicios N° 111</option>
              <option>Compañía de Sanidad y Veterinaria N° 111</option>
              <option>Compañía Comando y Servicios N° 111</option>
            </select>
            <textarea
              style={{ ...input, height:110, resize:"vertical" }}
              placeholder="Mensaje o preguntas adicionales"
              value={form.mensaje}
              onChange={e => setForm({...form, mensaje:e.target.value})}
              onFocus={e => e.target.style.borderColor="var(--gold)"}
              onBlur={e => e.target.style.borderColor="rgba(200,168,75,.25)"}
            />

            {/* Botón con ícono WhatsApp */}
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              style={{ width:"100%", fontSize:"1rem", display:"flex", alignItems:"center", justifyContent:"center", gap:".7rem" }}
            >
              <i className="bx bxl-whatsapp" style={{ fontSize:"1.3rem" }}></i>
              ENVIAR POR WHATSAPP
            </button>

            <p style={{ marginTop:".8rem", fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem", letterSpacing:1, color:"var(--muted)", textAlign:"center" }}>
              Al enviar serás redirigido a WhatsApp con tu información lista para enviar.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════════════════
function Footer({ onNav }) {
  return (
    <footer style={{ background:"#0a0c08", borderTop:"1px solid rgba(200,168,75,.2)", padding:"3rem 2rem 1.5rem" }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:"2rem", marginBottom:"2.5rem" }}>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.4rem", color:"var(--gold)", letterSpacing:3, marginBottom:"1rem" }}>1ra Brigada de Servicio</div>
            <p style={{ fontSize:".83rem", color:"var(--muted)", textAlign:"justify", lineHeight:1.7 }}>Inspirados en los principios de la logística militar, apoyo somos de la Región de Guerra de la Primera División.</p>
          </div>
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem", letterSpacing:"3px", textTransform:"uppercase", color:"var(--gold)", marginBottom:"1rem" }}>Páginas</div>
            {pages.map(p => (
              <button key={p.id} onClick={() => onNav(p.id)} style={{ display:"block", background:"none", border:"none", cursor:"pointer", color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontSize:".83rem", letterSpacing:1, padding:".25rem 0", transition:"color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color="var(--gold)"} onMouseLeave={e => e.currentTarget.style.color="var(--muted)"}
              >{p.label}</button>
            ))}
          </div>
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem", letterSpacing:"3px", textTransform:"uppercase", color:"var(--gold)", marginBottom:"1rem" }}>Contacto</div>
            <p style={{ fontSize:".83rem", color:"var(--muted)", lineHeight:2 }}>📞 +51 954 781 135<br/>📧 Jhonnycastro49@gmail.com<br/>🏛️ Cuartel "Soldado Isaac Rodríguez de Mendoza" Av. Fortunato Chirichigno S/N <br/> (Frente al coliseo deportivo Santa Lucia) - El Chipe, Piura, Perú<br/>🕐 Lunes — Viernes 08:00 Am – 3:00 Pm</p>
          </div>
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".75rem", letterSpacing:"3px", textTransform:"uppercase", color:"var(--gold)", marginBottom:"1rem" }}>Lema</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.3rem", letterSpacing:3, color:"var(--cream)", lineHeight:1.4 }}>APOYO<br/>OPORTUNO<br/>EFICAZ</div>
          </div>
        </div>
        <div style={{ borderTop:"1px solid rgba(200,168,75,.1)", paddingTop:"1.2rem", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:".5rem" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".72rem", letterSpacing:2, color:"rgba(212,197,160,.3)", textTransform:"uppercase" }}>© 2026 1Ra Brigada de servicio — Todos los derechos reservados</div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:".72rem", letterSpacing:2, color:"rgba(212,197,160,.3)", textTransform:"uppercase" }}>Apoyo · Oportuno · Eficaz</div>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [page, setPage] = useState("inicio");

  const handleNav = (id) => {
    setPage(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageMap = {
    inicio:       <PageInicio onNav={handleNav} />,
    bienvenida:       <PageBienvenida />,
    mision:       <PageMision />,
    valores:      <PageValores />,
    fuerzas:      <PageFuerzas />,
    historia:     <PageHistoria />,
    equipamiento: <PageEquipamiento />,
    noticias:     <PageNoticias />,
    enlistarse:   <PageEnlistarse />,
  };

  return (
    <>
      <style>{globalStyles}</style>
      <Navbar current={page} onNav={handleNav} />
      <main>{pageMap[page]}</main>
      <Footer onNav={handleNav} />
    </>
  );
}